class CacheBuster {
  constructor() {
    this.currentVersion = null;
    this.checkInterval = 30000; // Check every 30 seconds
    this.intervalId = null;
  }

  async getCurrentVersion() {
    try {
      // Add timestamp to prevent caching of version.json itself
      const response = await fetch(`/version.json?t=${Date.now()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch version');
      }
      const data = await response.json();
      return data.version;
    } catch (error) {
      console.warn('Cache buster: Failed to check version', error);
      return null;
    }
  }

  async initialize() {
    this.currentVersion = await this.getCurrentVersion();
    if (this.currentVersion) {
      console.log(`Cache buster: Initialized with version ${this.currentVersion}`);
      this.startVersionCheck();
    }
  }

  async checkForUpdates() {
    const newVersion = await this.getCurrentVersion();
    
    if (newVersion && this.currentVersion && newVersion !== this.currentVersion) {
      console.log(`Cache buster: New version detected (${this.currentVersion} -> ${newVersion})`);
      this.handleVersionUpdate();
      return true;
    }
    
    return false;
  }

  handleVersionUpdate() {
    // Clear all caches
    this.clearAllCaches();
    
    // Show user notification
    this.showUpdateNotification();
  }

  async clearAllCaches() {
    try {
      // Clear service worker caches if available
      if ('serviceWorker' in navigator && 'caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('Cache buster: Cleared all caches');
      }

      // Clear localStorage and sessionStorage
      localStorage.clear();
      sessionStorage.clear();
      
    } catch (error) {
      console.warn('Cache buster: Failed to clear caches', error);
    }
  }

  showUpdateNotification() {
    // Create a modern, non-intrusive notification
    const notification = document.createElement('div');
    notification.id = 'cache-buster-notification';
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
        color: white;
        padding: 16px 20px;
        border-radius: 12px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
        z-index: 10000;
        font-family: 'Inter', sans-serif;
        max-width: 320px;
        animation: slideInFromRight 0.3s ease-out;
      ">
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="font-size: 24px;">🚀</div>
          <div>
            <div style="font-weight: 600; margin-bottom: 4px;">New Version Available!</div>
            <div style="font-size: 14px; opacity: 0.9;">Click to refresh and get the latest updates</div>
          </div>
        </div>
        <button onclick="window.location.reload(true)" style="
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          margin-top: 12px;
          width: 100%;
          transition: all 0.2s ease;
        " onmouseover="this.style.background='rgba(255, 255, 255, 0.3)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.2)'">
          Refresh Now
        </button>
      </div>
    `;

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInFromRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(notification);

    // Auto-reload after 5 seconds if user doesn't click
    setTimeout(() => {
      if (document.getElementById('cache-buster-notification')) {
        window.location.reload(true);
      }
    }, 5000);
  }

  startVersionCheck() {
    this.intervalId = setInterval(() => {
      this.checkForUpdates();
    }, this.checkInterval);
  }

  stopVersionCheck() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // Method to update version file (for deployment scripts)
  static generateVersionFile() {
    const version = Date.now().toString();
    const versionData = {
      version,
      timestamp: new Date().toISOString()
    };
    return JSON.stringify(versionData, null, 2);
  }
}

export default CacheBuster;